import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2, Check } from "lucide-react";
import { IKKE_ALENE } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, type = "text", value, onChange, required, textarea }) => (
  <div className="relative">
    <label htmlFor={id} className="mb-2 block font-mono text-[11px] tracking-widest uppercase text-white/40">
      {label} {required && <span className="text-[#F59E0B]">*</span>}
    </label>
    {textarea ? (
      <textarea
        id={id}
        data-testid={`ikke-alene-${id}`}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/20 outline-none transition-all duration-300 focus:border-[#F59E0B]/60 focus:bg-white/[0.05] focus:shadow-[0_0_24px_rgba(245,158,11,0.15)]"
      />
    ) : (
      <input
        id={id}
        type={type}
        data-testid={`ikke-alene-${id}`}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-white placeholder-white/20 outline-none transition-all duration-300 focus:border-[#F59E0B]/60 focus:bg-white/[0.05] focus:shadow-[0_0_24px_rgba(245,158,11,0.15)]"
      />
    )}
  </div>
);

export const IkkeAleneForm = () => {
  const { t } = useLang();
  const c = IKKE_ALENE.collaborate;
  const f = c.form;
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const toggleInterest = (label) => {
    setInterests((prev) => (prev.includes(label) ? prev.filter((i) => i !== label) : [...prev, label]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t(f.error));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/ikke-alene`, {
        ...form,
        interests: interests.join(", "),
        consent,
      });
      toast.success(t(f.success));
      setForm({ name: "", email: "", organisation: "", message: "" });
      setInterests([]);
      setConsent(false);
    } catch (err) {
      toast.error(t(f.error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="ikke-alene-form"
      className="rounded-3xl border border-white/[0.07] bg-[#0A0E17]/80 backdrop-blur p-6 md:p-10"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="name" label={t(f.name)} value={form.name} onChange={set("name")} required />
        <Field id="email" label={t(f.email)} type="email" value={form.email} onChange={set("email")} required />
      </div>
      <div className="mt-5">
        <Field id="organisation" label={t(f.organisation)} value={form.organisation} onChange={set("organisation")} />
      </div>

      <div className="mt-5">
        <label className="mb-3 block font-mono text-[11px] tracking-widest uppercase text-white/40">{t(f.interest)}</label>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t(f.interest)}>
          {c.interests.map((item) => {
            const label = t(item);
            const active = interests.includes(label);
            return (
              <button
                key={label}
                type="button"
                onClick={() => toggleInterest(label)}
                data-testid={`ikke-alene-interest-${label}`}
                aria-pressed={active}
                className={`rounded-full border px-4 py-2 text-sm transition-all duration-300 ${
                  active
                    ? "border-[#F59E0B]/60 bg-[#F59E0B]/15 text-[#fcd9a0]"
                    : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/25 hover:text-white"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5">
        <Field id="message" label={t(f.message)} value={form.message} onChange={set("message")} required textarea />
      </div>
      <p className="mt-3 text-sm text-white/40 leading-relaxed">{t(f.helper)}</p>

      <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
        <span
          onClick={() => setConsent((v) => !v)}
          data-testid="ikke-alene-consent"
          role="checkbox"
          aria-checked={consent}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setConsent((v) => !v);
            }
          }}
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all duration-300 ${
            consent ? "border-[#F59E0B]/70 bg-[#F59E0B]/20" : "border-white/20 bg-white/[0.03]"
          }`}
        >
          {consent && <Check className="h-3.5 w-3.5 text-[#F59E0B]" />}
        </span>
        <span className="text-sm text-white/55 leading-relaxed">{t(f.consent)}</span>
      </label>

      <motion.button
        type="submit"
        disabled={loading}
        whileTap={{ scale: 0.98 }}
        data-testid="ikke-alene-submit-button"
        className="mt-7 inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#F59E0B] px-8 py-4 text-sm font-medium text-[#1a1204] transition-all duration-300 shadow-[0_0_30px_rgba(245,158,11,0.35)] hover:bg-[#fbbf4d] hover:shadow-[0_0_45px_rgba(245,158,11,0.55)] disabled:opacity-60"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {loading ? t(f.sending) : t(f.submit)}
      </motion.button>
    </form>
  );
};
